"use client";

import type { MouseEvent } from "react";
import { Radio } from "lucide-react";
import { cn } from "../lib/utils";

export function SidebarLiveCard({
  activeCount,
  active,
  href,
  onNavigate,
}: {
  activeCount: number;
  active: boolean;
  href: string;
  onNavigate: () => void;
}) {
  const activityLabel =
    activeCount === 0
      ? "Workspace quiet"
      : `${activeCount} ${activeCount === 1 ? "run" : "runs"} with recent signal`;

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button === 1
    ) {
      return;
    }
    event.preventDefault();
    onNavigate();
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      className={cn(
        "group relative mt-1 flex min-h-11 w-full items-center gap-2 overflow-hidden rounded-md border px-2 py-1.5 no-underline transition-colors focus-ring",
        active
          ? "border-[var(--border-strong)] bg-[var(--bg-active)] text-[var(--fg)]"
          : "border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-secondary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
      )}
      aria-current={active ? "page" : undefined}
      aria-label={`Live workspace. ${activityLabel}.`}
    >
      <span
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <span className="sidebar-live-wash absolute inset-0" />
        <span className="sidebar-live-orb sidebar-live-orb-a" />
        <span className="sidebar-live-orb sidebar-live-orb-b" />
        <span className="sidebar-live-orb sidebar-live-orb-c" />
      </span>

      <span className="relative z-[1] flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[color:color-mix(in_srgb,var(--purple)_28%,var(--border))] bg-[color:color-mix(in_srgb,var(--purple)_12%,var(--bg-secondary))] text-[var(--purple)]">
        <Radio size={14} />
      </span>
      <span className="relative z-[1] min-w-0 flex-1 text-left">
        <span className="block truncate text-[12.5px] font-medium text-[var(--fg)]">
          Live
        </span>
        <span className="block truncate text-[10.5px] text-[var(--fg-tertiary)]">
          {activityLabel}
        </span>
      </span>
      <span
        className="relative z-[1] flex h-2 w-2 shrink-0"
        aria-hidden="true"
      >
        {activeCount > 0 && (
          <span className="sidebar-live-pulse absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--purple)] opacity-50 motion-reduce:animate-none" />
        )}
        <span
          className={cn(
            "relative inline-flex h-2 w-2 rounded-full",
            activeCount > 0
              ? "bg-[var(--purple)]"
              : "bg-[var(--fg-muted)]"
          )}
        />
      </span>
    </a>
  );
}
